/**
 * MarketingScripts — injects analytics & tracking pixels controlled by env vars.
 * Only loads scripts if the corresponding env var is set (empty = disabled).
 *
 * Supported env vars (frontend/.env or Emergent env config):
 *   REACT_APP_GTM_ID           — Google Tag Manager container ID (e.g. GTM-XXXX)
 *   REACT_APP_GA4_ID           — Google Analytics 4 Measurement ID (e.g. G-XXXX)
 *   REACT_APP_META_PIXEL_ID    — Meta / Facebook Pixel ID (numeric)
 *   REACT_APP_TIKTOK_PIXEL_ID  — TikTok Pixel ID
 *
 * Verification meta tags (indexed at first render, not scripts):
 *   REACT_APP_GSC_VERIFICATION      — Google Search Console
 *   REACT_APP_BING_VERIFICATION     — Bing Webmaster
 *   REACT_APP_PINTEREST_VERIFICATION — Pinterest domain claim
 *
 * Best practice: use GTM alone and manage all other tags inside GTM.
 * Direct pixel installs are provided as fallbacks for teams without GTM.
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { getConsent } from "./CookieConsent";

const GTM_ID = process.env.REACT_APP_GTM_ID;
const GA4_ID = process.env.REACT_APP_GA4_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID;
const GSC_VERIFICATION = process.env.REACT_APP_GSC_VERIFICATION;
const BING_VERIFICATION = process.env.REACT_APP_BING_VERIFICATION;
const PINTEREST_VERIFICATION = process.env.REACT_APP_PINTEREST_VERIFICATION;

const MarketingScripts = () => {
  // Re-render when the visitor accepts / declines cookies so scripts inject/withhold live
  const [consent, setC] = useState(getConsent());
  useEffect(() => {
    const handler = () => setC(getConsent());
    window.addEventListener("endag1_consent_change", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("endag1_consent_change", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  // Marketing/tracking scripts require an explicit "granted" consent
  const marketingOK = consent === "granted";
  return (
    <Helmet>
      {/* -------- Search-engine verification meta -------- */}
      {GSC_VERIFICATION && (
        <meta name="google-site-verification" content={GSC_VERIFICATION} />
      )}
      {BING_VERIFICATION && (
        <meta name="msvalidate.01" content={BING_VERIFICATION} />
      )}
      {PINTEREST_VERIFICATION && (
        <meta name="p:domain_verify" content={PINTEREST_VERIFICATION} />
      )}

      {/* -------- Google Tag Manager (head) — consent-gated -------- */}
      {marketingOK && GTM_ID && (
        <script>{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');
        `}</script>
      )}

      {/* -------- Google Analytics 4 (gtag.js) -------- */}
      {marketingOK && GA4_ID && (
        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`}
        />
      )}
      {marketingOK && GA4_ID && (
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA4_ID}', { anonymize_ip: true });
        `}</script>
      )}

      {/* -------- Meta / Facebook Pixel -------- */}
      {marketingOK && META_PIXEL_ID && (
        <script>{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${META_PIXEL_ID}');
          fbq('track', 'PageView');
        `}</script>
      )}
      {marketingOK && META_PIXEL_ID && (
        <noscript>{`<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1" />`}</noscript>
      )}

      {/* -------- TikTok Pixel -------- */}
      {marketingOK && TIKTOK_PIXEL_ID && (
        <script>{`
          !function (w, d, t) {
            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=i+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
            ttq.load('${TIKTOK_PIXEL_ID}');
            ttq.page();
          }(window, document, 'ttq');
        `}</script>
      )}
    </Helmet>
  );
};

/**
 * GTMNoScript — renders the required <noscript> iframe fallback inside <body>.
 * Mount this once at the top of the app body, right after opening <body>.
 */
export const GTMNoScript = () => {
  if (!GTM_ID) return null;
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="gtm"
      />
    </noscript>
  );
};

export default MarketingScripts;
